import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { apiFetch, ApiError } from "@/lib/api-client";
import { formatDateTime } from "@/lib/i18n";

interface ServiceIntakeConfig {
  id: string;
  name: string;
  intakeFormId: string | null;
  intakeRequired: "first_booking" | "every_booking" | "never";
  pricingTiers?: Array<{
    name: string;
    priceAmount: number;
    creditCost: number;
  }>;
  bookingRules?: {
    lateCancelHours?: number;
    emergencyPassesMax?: number;
    noShowPolicy?: "charge_full" | "charge_percentage" | "block_rebooking" | "no_penalty";
    reminderSettings?: {
      enabled?: boolean;
      offsetsMinutes?: number[];
      channels?: Array<"email" | "sms">;
    };
  };
  updatedAt: string;
}

interface IntakeFormOption {
  id: string;
  name: string;
  status: string;
  mode?: string;
  updatedAt: string;
}

interface DraftState {
  intakeFormId: string | null;
  intakeRequired: "first_booking" | "every_booking" | "never";
  pricingTiers: Array<{
    name: string;
    priceAmount: string;
    creditCost: string;
  }>;
  lateCancelHours: string;
  emergencyPassesMax: string;
  noShowPolicy: "charge_full" | "charge_percentage" | "block_rebooking" | "no_penalty";
  remindersEnabled: boolean;
  reminderChannels: Array<"email" | "sms">;
  reminderOffsetsMinutes: string;
}

const EMPTY_ERROR = "";
const DEFAULT_REMINDER_OFFSETS = "1440, 120, 30";
const DEFAULT_REMINDER_CHANNELS: Array<"email" | "sms"> = ["email", "sms"];

function buildDraft(service: ServiceIntakeConfig): DraftState {
  const reminderSettings = service.bookingRules?.reminderSettings;
  return {
    intakeFormId: service.intakeFormId,
    intakeRequired: service.intakeRequired,
    pricingTiers: (service.pricingTiers ?? []).map((tier) => ({
      name: tier.name,
      priceAmount: String(tier.priceAmount),
      creditCost: String(tier.creditCost),
    })),
    lateCancelHours: service.bookingRules?.lateCancelHours === undefined ? "24" : String(service.bookingRules.lateCancelHours),
    emergencyPassesMax: service.bookingRules?.emergencyPassesMax === undefined ? "0" : String(service.bookingRules.emergencyPassesMax),
    noShowPolicy: service.bookingRules?.noShowPolicy ?? "no_penalty",
    remindersEnabled: reminderSettings?.enabled ?? true,
    reminderChannels: reminderSettings?.channels?.length ? reminderSettings.channels : DEFAULT_REMINDER_CHANNELS,
    reminderOffsetsMinutes: reminderSettings?.offsetsMinutes?.length
      ? reminderSettings.offsetsMinutes.join(", ")
      : DEFAULT_REMINDER_OFFSETS,
  };
}

function parseReminderOffsets(value: string): number[] {
  const offsets = value
    .split(",")
    .map((part) => Number.parseInt(part.trim(), 10))
    .filter((minutes) => Number.isFinite(minutes) && minutes >= 5 && minutes <= 10080);

  return [...new Set(offsets)].sort((left, right) => right - left);
}

export default function ServiceCatalogPage() {
  const [services, setServices] = useState<ServiceIntakeConfig[]>([]);
  const [forms, setForms] = useState<IntakeFormOption[]>([]);
  const [drafts, setDrafts] = useState<Record<string, DraftState>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState<string>(EMPTY_ERROR);
  const [savedId, setSavedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(EMPTY_ERROR);

      try {
        const [serviceRes, formsRes] = await Promise.all([
          apiFetch<{ data: ServiceIntakeConfig[] }>("/crm/bookings/services/intake"),
          apiFetch<{ data: IntakeFormOption[] }>("/crm/bookings/intake-forms"),
        ]);

        if (cancelled) {
          return;
        }

        setServices(serviceRes.data ?? []);
        setForms(formsRes.data ?? []);
        setDrafts(
          Object.fromEntries(
            (serviceRes.data ?? []).map((service) => [
              service.id,
              buildDraft(service),
            ]),
          ),
        );
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof ApiError
              ? loadError.message
              : loadError instanceof Error
                ? loadError.message
                : "Failed to load service intake settings.",
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const publishedForms = useMemo(
    () => forms.filter((form) => form.status !== "archived"),
    [forms],
  );

  async function saveDraft(serviceId: string) {
    const draft = drafts[serviceId];
    if (!draft) {
      return;
    }

    setSavingId(serviceId);
    setSavedId(null);
    setError(EMPTY_ERROR);

    try {
      const response = await apiFetch<{ data: Pick<ServiceIntakeConfig, "intakeFormId" | "intakeRequired" | "pricingTiers" | "bookingRules" | "updatedAt"> }>("/crm/bookings/services/" + serviceId + "/intake-config", {
        method: "PATCH",
        body: JSON.stringify({
          intakeFormId: draft.intakeFormId,
          intakeRequired: draft.intakeRequired,
          pricingTiers: draft.pricingTiers.map((tier) => ({
            name: tier.name.trim(),
            priceAmount: Number(tier.priceAmount),
            creditCost: Number(tier.creditCost),
          })),
          bookingRules: {
            lateCancelHours: Number(draft.lateCancelHours),
            emergencyPassesMax: Number(draft.emergencyPassesMax),
            noShowPolicy: draft.noShowPolicy,
            reminderSettings: {
              enabled: draft.remindersEnabled,
              offsetsMinutes: parseReminderOffsets(draft.reminderOffsetsMinutes),
              channels: draft.reminderChannels.length > 0 ? draft.reminderChannels : DEFAULT_REMINDER_CHANNELS,
            },
          },
        }),
      });

      setServices((current) =>
        current.map((service) => (
          service.id === serviceId
            ? {
                ...service,
                intakeFormId: response.data.intakeFormId,
                intakeRequired: response.data.intakeRequired,
                pricingTiers: response.data.pricingTiers,
                bookingRules: response.data.bookingRules,
                updatedAt: response.data.updatedAt,
              }
            : service
        )),
      );
      setSavedId(serviceId);
    } catch (saveError) {
      setError(
        saveError instanceof ApiError
          ? saveError.message
          : saveError instanceof Error
            ? saveError.message
            : "Failed to save intake configuration.",
      );
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-background)] p-6 shadow-[0_24px_70px_-50px_rgba(15,23,42,0.45)]">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--color-primary)]">Mounted service intake controls</p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight text-[var(--color-foreground)]">Service catalog intake settings</h1>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-[var(--color-muted-foreground)]">
          Link a live Stero form to each service and decide whether it runs for every booking, only the first booking,
          or never. This screen is wired to the same booking runtime the public widget uses.
        </p>
      </div>

      {error && (
        <div className="rounded-2xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/6 p-4 text-sm text-[var(--color-error)]">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-background)] p-8 text-sm text-[var(--color-muted-foreground)]">
          Loading service intake settings...
        </div>
      ) : (
        <div className="overflow-hidden rounded-3xl border border-[var(--color-border)] bg-[var(--color-background)]">
          <table className="min-w-full divide-y divide-[var(--color-border)]">
            <thead className="bg-[var(--color-surface)]">
              <tr className="text-left text-xs uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
                <th className="px-5 py-4 font-medium">Service</th>
                <th className="px-5 py-4 font-medium">Intake form</th>
                <th className="px-5 py-4 font-medium">When to require it</th>
                <th className="px-5 py-4 font-medium">Booking rules</th>
                <th className="px-5 py-4 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {services.map((service) => {
                const draft = drafts[service.id] ?? buildDraft(service);

                return (
                  <tr key={service.id}>
                    <td className="px-5 py-4">
                      <div className="font-medium text-[var(--color-foreground)]">{service.name}</div>
                      <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                        Updated {formatDateTime(service.updatedAt)}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <select
                        value={draft.intakeFormId ?? ""}
                        onChange={(event) => {
                          const value = event.target.value || null;
                          setDrafts((current) => ({
                            ...current,
                            [service.id]: {
                              ...draft,
                              intakeFormId: value,
                            },
                          }));
                        }}
                        className="h-11 min-w-[220px] rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                      >
                        <option value="">No intake form</option>
                        {publishedForms.map((form) => (
                          <option key={form.id} value={form.id}>
                            {form.name} · {form.mode ?? "classic"} · {form.status}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4">
                      <select
                        value={draft.intakeRequired}
                        onChange={(event) => {
                          setDrafts((current) => ({
                            ...current,
                            [service.id]: {
                              ...draft,
                              intakeRequired: event.target.value as DraftState["intakeRequired"],
                            },
                          }));
                        }}
                        className="h-11 min-w-[220px] rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                      >
                        <option value="never">Never</option>
                        <option value="first_booking">First booking only</option>
                        <option value="every_booking">Every booking</option>
                      </select>
                    </td>
                    <td className="px-5 py-4">
                      <div className="grid min-w-[320px] gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Pricing tiers</span>
                            <button
                              type="button"
                              onClick={() => {
                                setDrafts((current) => ({
                                  ...current,
                                  [service.id]: {
                                    ...draft,
                                    pricingTiers: [
                                      ...draft.pricingTiers,
                                      { name: "", priceAmount: "0", creditCost: "0" },
                                    ],
                                  },
                                }));
                              }}
                              className="rounded-xl border border-[var(--color-border)] px-3 py-1 text-xs font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                            >
                              Add tier
                            </button>
                          </div>
                          {draft.pricingTiers.length === 0 ? (
                            <p className="text-sm text-[var(--color-muted-foreground)]">No pricing tiers configured. Add packages or bundled options here.</p>
                          ) : (
                            <div className="space-y-2">
                              {draft.pricingTiers.map((tier, index) => (
                                <div key={`${service.id}-tier-${index}`} className="grid gap-2 rounded-2xl border border-[var(--color-border)] p-3 md:grid-cols-[1.4fr_1fr_1fr_auto]">
                                  <input
                                    type="text"
                                    value={tier.name}
                                    placeholder="Tier name"
                                    onChange={(event) => {
                                      const pricingTiers = [...draft.pricingTiers];
                                      pricingTiers[index] = { ...tier, name: event.target.value };
                                      setDrafts((current) => ({
                                        ...current,
                                        [service.id]: {
                                          ...draft,
                                          pricingTiers,
                                        },
                                      }));
                                    }}
                                    className="h-11 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                                  />
                                  <input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={tier.priceAmount}
                                    placeholder="Price"
                                    onChange={(event) => {
                                      const pricingTiers = [...draft.pricingTiers];
                                      pricingTiers[index] = { ...tier, priceAmount: event.target.value };
                                      setDrafts((current) => ({
                                        ...current,
                                        [service.id]: {
                                          ...draft,
                                          pricingTiers,
                                        },
                                      }));
                                    }}
                                    className="h-11 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                                  />
                                  <input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={tier.creditCost}
                                    placeholder="Credits"
                                    onChange={(event) => {
                                      const pricingTiers = [...draft.pricingTiers];
                                      pricingTiers[index] = { ...tier, creditCost: event.target.value };
                                      setDrafts((current) => ({
                                        ...current,
                                        [service.id]: {
                                          ...draft,
                                          pricingTiers,
                                        },
                                      }));
                                    }}
                                    className="h-11 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setDrafts((current) => ({
                                        ...current,
                                        [service.id]: {
                                          ...draft,
                                          pricingTiers: draft.pricingTiers.filter((_, tierIndex) => tierIndex !== index),
                                        },
                                      }));
                                    }}
                                    className="h-11 rounded-2xl border border-[var(--color-border)] px-3 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <label className="space-y-1 text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                          <span>Late cancel deadline</span>
                          <input
                            type="number"
                            min={0}
                            value={draft.lateCancelHours}
                            onChange={(event) => {
                              setDrafts((current) => ({
                                ...current,
                                [service.id]: {
                                  ...draft,
                                  lateCancelHours: event.target.value,
                                },
                              }));
                            }}
                            className="h-11 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm normal-case tracking-normal text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                          />
                        </label>
                        <label className="space-y-1 text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                          <span>No-show policy</span>
                          <select
                            value={draft.noShowPolicy}
                            onChange={(event) => {
                              setDrafts((current) => ({
                                ...current,
                                [service.id]: {
                                  ...draft,
                                  noShowPolicy: event.target.value as DraftState["noShowPolicy"],
                                },
                              }));
                            }}
                            className="h-11 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm normal-case tracking-normal text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                          >
                            <option value="no_penalty">No penalty</option>
                            <option value="charge_full">Charge full</option>
                            <option value="charge_percentage">Charge percentage</option>
                            <option value="block_rebooking">Block rebooking</option>
                          </select>
                        </label>
                        <label className="space-y-1 text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                          <span>Emergency passes</span>
                          <input
                            type="number"
                            min={0}
                            value={draft.emergencyPassesMax}
                            onChange={(event) => {
                              setDrafts((current) => ({
                                ...current,
                                [service.id]: {
                                  ...draft,
                                  emergencyPassesMax: event.target.value,
                                },
                              }));
                            }}
                            className="h-11 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm normal-case tracking-normal text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                          />
                        </label>
                        <div className="rounded-2xl border border-[var(--color-border)] p-3">
                          <label className="flex items-center gap-2 text-sm font-medium text-[var(--color-foreground)]">
                            <input
                              type="checkbox"
                              checked={draft.remindersEnabled}
                              onChange={(event) => {
                                setDrafts((current) => ({
                                  ...current,
                                  [service.id]: {
                                    ...draft,
                                    remindersEnabled: event.target.checked,
                                  },
                                }));
                              }}
                            />
                            SMS + email reminders
                          </label>
                          <div className="mt-3 grid gap-3 md:grid-cols-2">
                            <label className="space-y-1 text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                              <span>Reminder timing minutes before start</span>
                              <input
                                type="text"
                                value={draft.reminderOffsetsMinutes}
                                placeholder={DEFAULT_REMINDER_OFFSETS}
                                onChange={(event) => {
                                  setDrafts((current) => ({
                                    ...current,
                                    [service.id]: {
                                      ...draft,
                                      reminderOffsetsMinutes: event.target.value,
                                    },
                                  }));
                                }}
                                className="h-11 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm normal-case tracking-normal text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                              />
                            </label>
                            <div className="space-y-2 text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                              <span>Channels</span>
                              <div className="flex flex-wrap gap-3 text-sm normal-case tracking-normal text-[var(--color-foreground)]">
                                {DEFAULT_REMINDER_CHANNELS.map((channel) => (
                                  <label key={channel} className="inline-flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={draft.reminderChannels.includes(channel)}
                                      onChange={(event) => {
                                        const nextChannels = event.target.checked
                                          ? [...new Set([...draft.reminderChannels, channel])]
                                          : draft.reminderChannels.filter((value) => value !== channel);
                                        setDrafts((current) => ({
                                          ...current,
                                          [service.id]: {
                                            ...draft,
                                            reminderChannels: nextChannels,
                                          },
                                        }));
                                      }}
                                    />
                                    {channel.toUpperCase()}
                                  </label>
                                ))}
                              </div>
                            </div>
                          </div>
                          <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                            Booking create and reschedule now use these settings; cancel marks unsent reminders as cancelled.
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => void saveDraft(service.id)}
                          disabled={savingId === service.id}
                          className="inline-flex h-11 items-center justify-center rounded-2xl bg-[var(--color-primary)] px-4 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                        >
                          {savingId === service.id ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Saving
                            </>
                          ) : "Save"}
                        </button>
                        {savedId === service.id && (
                          <span className="inline-flex items-center gap-1 text-sm text-[var(--color-success)]">
                            <CheckCircle2 className="h-4 w-4" />
                            Saved
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
